import React, { useCallback, useEffect, useState } from "react"

import { NIRVANA_COLORS } from "@front-shared/config/theme.config"
import { ButtonElement } from "@front-shared/elements/button.element"
import { PopupWrapperElement } from "@front-shared/elements/popupWrapper.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"
import { ToastrHelper } from "@front-shared/helpers/toastr.helper"
import type { IPopupProps } from "@front-shared/types/props.types"

import {
    BULK_UPLOAD_CONFIG,
    BulkUploadStage,
} from "../../config/bulkUpload.config"
import { ParsingAnimationElement } from "../../elements/parsingAnimation.element"
import { useFileDropzone } from "../../hooks/fileDropzone.hook"

import trashIcon from "@front-shared/assets/images/icons/trash-dark2.svg"

export const BulkUploadPopupComponent = <TItem,>(
    props: IBulkUploadPopupComponentProps<TItem>,
) => {
    const [stage, setStage] = useState<BulkUploadStage>(BulkUploadStage.INITIAL)
    const [selectedFile, setSelectedFile] = useState<File | null>(null)

    const [parsingErrorMessage, setParsingErrorMessage] = useState<
        string | null
    >(null)

    const onRemoveFile = useCallback(() => {
        setSelectedFile(null)
        setParsingErrorMessage(null)
        setStage(BulkUploadStage.INITIAL)
    }, [])

    useEffect(() => {
        if (props.isActive) {
            return
        }

        onRemoveFile()
    }, [props.isActive, onRemoveFile])

    const onDownloadTemplate = () => {
        window.open(props.templateUrl, "_blank", "noopener,noreferrer")
    }

    const handleFile = (file: File) => {
        if (!BULK_UPLOAD_CONFIG.isAcceptedCsvFile(file)) {
            ToastrHelper.error("Please upload a CSV file")

            return
        }

        setSelectedFile(file)
        setStage(BulkUploadStage.STAGING)
    }

    const { fileInputProps, dropzoneProps } = useFileDropzone({
        accept: BULK_UPLOAD_CONFIG.csvAccept,
        onFilesSelected: files => {
            handleFile(files[0])
        },
    })

    const runBulkUpload = async () => {
        if (!selectedFile || props.disabled) {
            return
        }

        setStage(BulkUploadStage.PARSING)

        try {
            const items = props.parseCsv(await selectedFile.text())

            if (!items.length) {
                setParsingErrorMessage("No valid data rows found in CSV")
                setStage(BulkUploadStage.PARSING_ERROR)

                return
            }

            props.onValidate(items, selectedFile, {
                onSuccess: () => {
                    props.onDone()
                },
                onError: () => {
                    setStage(BulkUploadStage.STAGING)
                },
            })
        } catch {
            setParsingErrorMessage("Unable to parse this CSV")
            setStage(BulkUploadStage.PARSING_ERROR)
        }
    }

    const renderContent = () => {
        if (stage === BulkUploadStage.PARSING_ERROR) {
            return (
                <>
                    <PrimaryText
                        typography="subtitleSemibold"
                        className="
                            mt-[24px] flex items-center justify-between gap-[12px]
                            rounded-[8px] border border-solid border-brand-black-transparent-010
                            bg-brand-offWhite py-[16px] px-[24px]
                        "
                    >
                        {parsingErrorMessage ??
                            "No valid data rows found in CSV"}
                    </PrimaryText>

                    <div className="flex justify-end mt-[24px]">
                        <ButtonElement
                            label="Done"
                            type="primary"
                            htmlType="button"
                            onClick={props.onDone}
                        />
                    </div>
                </>
            )
        }

        if (stage === BulkUploadStage.PARSING) {
            return (
                <div
                    className="
                        mt-[24px] py-[16px] px-[24px] rounded-[8px] bg-brand-offWhite flex flex-col gap-[8px]
                    "
                >
                    <PrimaryText typography="subtitleSemibold">
                        {props.parsingLabel}
                    </PrimaryText>

                    <PrimaryText>
                        This can take several minutes. Please keep this tab open
                        until it completes.
                    </PrimaryText>

                    <ParsingAnimationElement />
                </div>
            )
        }

        if (stage === BulkUploadStage.STAGING && selectedFile) {
            return (
                <>
                    <div
                        className="
                            mt-[24px] flex items-center justify-between gap-[12px]
                            rounded-[8px] border border-solid border-brand-black-transparent-010
                            bg-brand-offWhite py-[16px] px-[24px]
                        "
                    >
                        <PrimaryText
                            typography="subtitleSemibold"
                            className="truncate flex-1 min-w-0"
                        >
                            {selectedFile.name}
                        </PrimaryText>

                        <button
                            type="button"
                            onClick={onRemoveFile}
                            className="flex-shrink-0 p-[8px] cursor-pointer hover:opacity-80"
                            aria-label="Remove file"
                        >
                            <img
                                src={trashIcon}
                                alt="Remove file"
                                className="w-[16px] h-[16px]"
                            />
                        </button>
                    </div>

                    <div className="flex justify-between gap-[12px] mt-[24px]">
                        <ButtonElement
                            label="Cancel"
                            type="default"
                            htmlType="button"
                            onClick={onRemoveFile}
                        />

                        <ButtonElement
                            label={props.importButtonLabel}
                            type="primary"
                            htmlType="button"
                            onClick={runBulkUpload}
                            disabled={props.disabled}
                        />
                    </div>
                </>
            )
        }

        return (
            <>
                <input {...fileInputProps} />

                <div
                    className="
                        flex flex-col items-center justify-center cursor-pointer
                        min-h-[160px] p-[24px] mt-[24px] rounded-[8px] gap-[8px]
                        border-2 border-dashed border-brand-warmShadow
                    "
                    {...dropzoneProps}
                >
                    <PrimaryText typography="subtitleSemibold">
                        {props.dropzoneLabel}
                    </PrimaryText>

                    <PrimaryText
                        typography="subtitle"
                        color={NIRVANA_COLORS.brand.warmShadow}
                    >
                        Drop your CSV in here, or{" "}
                        <span className="text-brand-vibrantPurple underline cursor-pointer">
                            manually select
                        </span>
                    </PrimaryText>
                </div>
            </>
        )
    }

    return (
        <PopupWrapperElement
            isActive={props.isActive}
            className={props.className}
            onDone={props.onDone}
            closeOnOutsideClick={props.closeOnOutsideClick}
            withCloseIcon={stage !== BulkUploadStage.PARSING}
            popupWidthClassName="!w-[560px] !bg-brand-white"
        >
            <div className="pt-[8px]">
                <PrimaryText
                    typography="h5"
                    color={NIRVANA_COLORS.brand.black}
                    className="mb-[8px]"
                >
                    {props.title}
                </PrimaryText>

                {stage !== BulkUploadStage.PARSING ? (
                    <>
                        <PrimaryText
                            typography="subtitle"
                            color={NIRVANA_COLORS.brand.warmShadow}
                        >
                            {props.description}
                        </PrimaryText>

                        <button
                            type="button"
                            onClick={onDownloadTemplate}
                            className="
                                text-left p-0 border-0 bg-transparent cursor-pointer
                                hover:opacity-80
                            "
                        >
                            <PrimaryText typography="subtitle" underline>
                                Download template
                            </PrimaryText>
                        </button>
                    </>
                ) : null}

                {props.notice && stage !== BulkUploadStage.PARSING ? (
                    <div className="mt-[16px]">{props.notice}</div>
                ) : null}

                {renderContent()}
            </div>
        </PopupWrapperElement>
    )
}

interface IBulkUploadCallbacks {
    onSuccess: () => void
    onError: () => void
}

interface IBulkUploadPopupComponentProps<TItem> extends IPopupProps {
    title: string
    description: string
    dropzoneLabel: string
    importButtonLabel: string
    parsingLabel: string
    templateUrl: string
    notice?: React.ReactNode
    disabled?: boolean
    parseCsv: (csvText: string) => TItem[]
    onValidate: (
        items: TItem[],
        file: File,
        callbacks: IBulkUploadCallbacks,
    ) => void
}
